import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

function hexToRgb(hex: string): [number, number, number] {
  const trimmed = hex.replace("#", "");
  const r = parseInt(trimmed.substring(0, 2), 16);
  const g = parseInt(trimmed.substring(2, 4), 16);
  const b = parseInt(trimmed.substring(4, 6), 16);
  return [r, g, b];
}

function rgbToHex(r: number, g: number, b: number): string {
  const hr = r.toString(16).padStart(2, "0");
  const hg = g.toString(16).padStart(2, "0");
  const hb = b.toString(16).padStart(2, "0");
  return `#${hr}${hg}${hb}`;
}

function interpolateColor(hex1: string, hex2: string, factor: number): string {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);

  const r = Math.round(c1[0] + factor * (c2[0] - c1[0]));
  const g = Math.round(c1[1] + factor * (c2[1] - c1[1]));
  const b = Math.round(c1[2] + factor * (c2[2] - c1[2]));

  return rgbToHex(r, g, b);
}

interface SingleProgressBarProps {
  value: number;
  total: number;
  height?: number;
  barSize?: number;
  startColor?: string;
  endColor?: string;
  backgroundColor?: string;
}

export const SingleProgressBar: React.FC<SingleProgressBarProps> = ({
  value,
  total,
  height = 15,
  barSize = 4,
  startColor = "#DD503E",
  endColor = "#66EF64",
  backgroundColor = "#e0e0e0",
}) => {
  const ratio = total > 0 ? Math.min(Math.max(value / total, 0), 1) : 0;
  const barColor = interpolateColor(startColor, endColor, ratio);
  const data = [{ value }];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 5, right: 20, bottom: 5, left: 20 }}
        innerRadius={10}
      >
        <XAxis type="number" domain={[0, total]} hide />
        <YAxis type="category" hide dataKey="name" />
        <CartesianGrid horizontal={false} vertical={false} />

        <Bar
          dataKey="value"
          fill={barColor}
          background={{ fill: backgroundColor }}
          barSize={barSize}
          radius={10}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
